import { useState } from "react";
import { Plus, Printer, Save, Search, Trash2 } from "lucide-react";
import { fabricCuttingApi as api } from "../../api/fabricCuttingApi.js";

const n = (value) => Number(value || 0);
const fixed = (value) => n(value).toFixed(3);
const blankBatch = (colour = "", dia = "") => ({
  colour,
  dia,
  bundleNo: "",
  weightKg: "",
});

export function FoldingDocument({
  plan,
  lines = [],
  batches = [],
  quality,
  companyName = "UG SaaS",
}) {
  const colours = [...new Set(lines.map((row) => row.colour))];
  const sizes = [...new Set(lines.map((row) => row.size))];
  const sizeLine = (colour, size) =>
    lines.find((row) => row.colour === colour && row.size === size);
  const batchesFor = (colour) => {
    const found = batches.filter((row) => row.colour === colour);
    return found.length
      ? found
      : [blankBatch(colour, lines.find((row) => row.colour === colour)?.dia)];
  };
  const sizeSummary = sizes.map((size) => {
    const rows = lines.filter((row) => row.size === size);
    return {
      size,
      pieceWeight: n(rows[0]?.foldingWeightPerPieceKg),
      pcs: rows.reduce((sum, row) => sum + n(row.actualCuttingPcs), 0),
      wanted: rows.reduce((sum, row) => sum + n(row.wantedWeightKg), 0),
    };
  });
  const sizeGroups = Array.from(
    { length: Math.ceil(sizes.length / 4) },
    (_, index) => sizes.slice(index * 4, index * 4 + 4),
  );
  const colourRows = colours.flatMap((colour, colourIndex) => {
    const colourBatches = batchesFor(colour);
    return colourBatches.map((batch, batchIndex) => ({
      colour,
      colourIndex,
      batch,
      batchIndex,
      span: colourBatches.length,
    }));
  });

  return (
    <article className="production-plan-document folding-document folding-excel-layout scoped-print-target">
      <header>
        <h1>{companyName}</h1>
        <b>FABRIC DEPARTMENT · FOLDING PLAN</b>
      </header>
      <div className="folding-top-grid">
        <table className="folding-meta-table">
          <tbody>
            <tr>
              <th>Date</th>
              <td>
                {new Date(plan.createdAt || Date.now()).toLocaleDateString()}
              </td>
            </tr>
            <tr>
              <th>Slip / Plan No</th>
              <td>{plan.planNo}</td>
            </tr>
            <tr>
              <th>Item Name</th>
              <td>{plan.itemName}</td>
            </tr>
            <tr>
              <th>Order No</th>
              <td>{plan.orderNo}</td>
            </tr>
            <tr>
              <th>Folding Quality</th>
              <td>{quality || "—"}</td>
            </tr>
          </tbody>
        </table>
        <table className="folding-summary-table">
          <thead>
            <tr>
              <th>Size</th>
              <th>PCS WT</th>
              <th>PCS</th>
              <th>F.WT</th>
              <th>Bundle</th>
              <th>T.WT</th>
            </tr>
          </thead>
          <tbody>
            {sizeSummary.map((row) => (
              <tr key={row.size}>
                <td>{row.size}</td>
                <td>{fixed(row.pieceWeight)}</td>
                <td>{row.pcs}</td>
                <td>{fixed(row.wanted)}</td>
                <td></td>
                <td></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>TOTAL</th>
              <th></th>
              <th>{sizeSummary.reduce((sum, row) => sum + row.pcs, 0)}</th>
              <th>
                {fixed(sizeSummary.reduce((sum, row) => sum + row.wanted, 0))}
              </th>
              <th></th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>
      {sizeGroups.map((group, groupIndex) => (
        <section className="folding-size-block" key={group.join("-")}>
          <h3>
            Size-wise Folding Requirement{" "}
            {sizeGroups.length > 1 ? `· Part ${groupIndex + 1}` : ""}
          </h3>
          <table className="folding-size-matrix">
            <thead>
              <tr>
                <th rowSpan="2">No</th>
                <th rowSpan="2">Batch No</th>
                <th rowSpan="2">Colour</th>
                {group.map((size) => (
                  <th colSpan="3" className="size-group" key={size}>
                    Size {size}
                  </th>
                ))}
              </tr>
              <tr>
                {group.flatMap((size) => [
                  <th key={`${size}-pcs`}>PCS</th>,
                  <th key={`${size}-want`}>Wanted WT</th>,
                  <th key={`${size}-actual`}>Actual WT</th>,
                ])}
              </tr>
            </thead>
            <tbody>
              {colourRows.map(({ colour, colourIndex, batch, batchIndex }) => (
                <tr key={`${colour}-${batchIndex}`}>
                  <td>{colourIndex + 1}</td>
                  <td>{batch.bundleNo || ""}</td>
                  <td>{colour}</td>
                  {group.flatMap((size) => {
                    const row = sizeLine(colour, size) || {};
                    return [
                      <td key={`${size}-pcs`}>
                        {batchIndex === 0 ? n(row.actualCuttingPcs) || "" : ""}
                      </td>,
                      <td key={`${size}-want`}>
                        {batchIndex === 0 && row.wantedWeightKg != null
                          ? fixed(row.wantedWeightKg)
                          : ""}
                      </td>,
                      <td key={`${size}-actual`}>
                        {batchIndex === 0 ? row.actualWeightKg || "" : ""}
                      </td>,
                    ];
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="3">TOTAL</th>
                {group.flatMap((size) => {
                  const rows = lines.filter((row) => row.size === size);
                  return [
                    <th key={`${size}-pcs`}>
                      {rows.reduce(
                        (sum, row) => sum + n(row.actualCuttingPcs),
                        0,
                      )}
                    </th>,
                    <th key={`${size}-want`}>
                      {fixed(
                        rows.reduce(
                          (sum, row) => sum + n(row.wantedWeightKg),
                          0,
                        ),
                      )}
                    </th>,
                    <th key={`${size}-actual`}>
                      {fixed(
                        rows.reduce(
                          (sum, row) => sum + n(row.actualWeightKg),
                          0,
                        ),
                      )}
                    </th>,
                  ];
                })}
              </tr>
            </tfoot>
          </table>
        </section>
      ))}
      <h3>Consolidated Colour / Batch Summary</h3>
      <table>
        <thead>
          <tr>
            <th>S.No</th>
            <th>Colour</th>
            <th>Batch No</th>
            <th>Dia</th>
            <th>Folding Net WT</th>
            <th>Actual FL.WT</th>
          </tr>
        </thead>
        <tbody>
          {colourRows.map(
            ({ colour, colourIndex, batch, batchIndex, span }) => {
              const colourLines = lines.filter((row) => row.colour === colour);
              return (
                <tr key={`${colour}-summary-${batchIndex}`}>
                  {batchIndex === 0 && (
                    <>
                      <td rowSpan={span}>{colourIndex + 1}</td>
                      <td rowSpan={span}>{colour}</td>
                    </>
                  )}
                  <td>{batch.bundleNo || ""}</td>
                  <td>{batch.dia || colourLines[0]?.dia || "—"}</td>
                  {batchIndex === 0 && (
                    <>
                      <td rowSpan={span}>
                        {fixed(
                          colourLines.reduce(
                            (sum, row) => sum + n(row.wantedWeightKg),
                            0,
                          ),
                        )}
                      </td>
                      <td rowSpan={span}>
                        {fixed(
                          colourLines.reduce(
                            (sum, row) => sum + n(row.actualWeightKg),
                            0,
                          ),
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            },
          )}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan="4">TOTAL</th>
            <th>
              {fixed(
                lines.reduce((sum, row) => sum + n(row.wantedWeightKg), 0),
              )}
            </th>
            <th>
              {fixed(
                lines.reduce((sum, row) => sum + n(row.actualWeightKg), 0),
              )}
            </th>
          </tr>
        </tfoot>
      </table>
      <div className="document-notes">
        <b>Remarks:</b>
      </div>
      <div className="cutting-signatures">
        <span>GRN No</span>
        <span>Prepared By</span>
        <span>Checked By</span>
        <span>Approved By</span>
      </div>
    </article>
  );
}

export default function FoldingEntryPage({ notify }) {
  const [number, setNumber] = useState("");
  const [plan, setPlan] = useState(null);
  const [lines, setLines] = useState([]);
  const [batches, setBatches] = useState([]);
  const [quality, setQuality] = useState("");
  const [busy, setBusy] = useState(false);
  async function find() {
    setBusy(true);
    try {
      const { plan: loadedPlan, actual, item } = await api.foldingSetup(number);
      if (!actual)
        throw new Error("Cutting Actual must be saved before Folding Entry");
      setPlan(loadedPlan);
      setQuality(loadedPlan.foldingQuality || "");
      setLines(
        loadedPlan.foldingLines?.length
          ? loadedPlan.foldingLines
          : actual.lines.map((row) => {
              const bom = item?.sizes?.find(
                (size) =>
                  String(size.size).toUpperCase() ===
                  String(row.size).toUpperCase(),
              );
              const perPiece = n(bom?.foldingPieceWeightKg);
              return {
                colour: row.colour,
                size: row.size,
                dia: row.dia,
                actualCuttingPcs: row.actualPcs,
                foldingWeightPerPieceKg: perPiece,
                wantedWeightKg: Number((row.actualPcs * perPiece).toFixed(3)),
                actualWeightKg: "",
              };
            }),
      );
      setBatches(
        loadedPlan.foldingBatches?.length
          ? loadedPlan.foldingBatches
          : [
              ...new Map(
                actual.lines.map((row) => [
                  `${row.colour}|${row.dia}`,
                  blankBatch(row.colour, row.dia),
                ]),
              ).values(),
            ],
      );
    } catch (error) {
      setPlan(null);
      notify?.(error.message);
    } finally {
      setBusy(false);
    }
  }
  const updateLine = (index, value) =>
    setLines(
      lines.map((row, rowIndex) =>
        rowIndex === index ? { ...row, actualWeightKg: value } : row,
      ),
    );
  const updateBatch = (index, key, value) =>
    setBatches(
      batches.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    );
  async function save() {
    setBusy(true);
    try {
      const saved = await api.saveFolding(plan.planNo, {
        foldingQuality: quality,
        lines,
        batches,
      });
      setPlan(saved);
      notify?.(
        "Folding entry saved; entered Actual Weight reduced from Fabric Balance Stock",
      );
    } catch (error) {
      notify?.(error.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="classic-page">
      <div className="classic-title">
        <div>
          <small>FABRIC DEPARTMENT</small>
          <h2>Folding Entry</h2>
          <p>
            Size-wise actual weight entry and batch-wise Fabric Balance Stock
            reduction.
          </p>
        </div>
      </div>
      <div className="classic-card print-search">
        <label>
          <span>Plan No / DC No</span>
          <div className="input-action">
            <input
              value={number}
              onChange={(event) => setNumber(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && find()}
            />
            <button onClick={find}>
              <Search />
            </button>
          </div>
        </label>
      </div>
      {plan && (
        <>
          <div className="classic-card">
            <label>
              Folding Quality
              <input
                value={quality}
                onChange={(event) => setQuality(event.target.value)}
              />
            </label>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Colour</th>
                    <th>Size</th>
                    <th>Dia</th>
                    <th>Cutting PCS</th>
                    <th>WT/PCS</th>
                    <th>Wanted WT</th>
                    <th>Actual WT</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((row, index) => (
                    <tr key={`${row.colour}-${row.size}`}>
                      <td>{row.colour}</td>
                      <td>{row.size}</td>
                      <td>{row.dia}</td>
                      <td>{row.actualCuttingPcs}</td>
                      <td>{row.foldingWeightPerPieceKg}</td>
                      <td>{row.wantedWeightKg}</td>
                      <td>
                        <input
                          type="number"
                          step=".001"
                          value={row.actualWeightKg}
                          onChange={(event) =>
                            updateLine(index, event.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3>Manual Batch Allocation</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Colour</th>
                    <th>Dia</th>
                    <th>Batch No</th>
                    <th>Actual WT</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch, index) => (
                    <tr key={index}>
                      <td>{batch.colour}</td>
                      <td>
                        <input
                          value={batch.dia}
                          onChange={(event) =>
                            updateBatch(index, "dia", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={batch.bundleNo}
                          onChange={(event) =>
                            updateBatch(index, "bundleNo", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step=".001"
                          value={batch.weightKg}
                          onChange={(event) =>
                            updateBatch(index, "weightKg", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() =>
                            setBatches([
                              ...batches.slice(0, index + 1),
                              blankBatch(batch.colour, batch.dia),
                              ...batches.slice(index + 1),
                            ])
                          }
                        >
                          <Plus /> Batch
                        </button>
                        {batches.length > 1 && (
                          <button
                            type="button"
                            className="danger"
                            onClick={() =>
                              setBatches(
                                batches.filter(
                                  (_, rowIndex) => rowIndex !== index,
                                ),
                              )
                            }
                          >
                            <Trash2 />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => window.print()}>
                <Printer /> Blank / Filled Print
              </button>
              <button
                type="button"
                className="primary"
                disabled={busy || plan.foldingLines?.length}
                onClick={save}
              >
                <Save />{" "}
                {plan.foldingLines?.length
                  ? "Folding Entry Saved"
                  : "Save Changes"}
              </button>
            </div>
          </div>
          <FoldingDocument
            plan={plan}
            lines={lines}
            batches={batches}
            quality={quality}
          />
        </>
      )}
    </section>
  );
}
